import React, { useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '../ui/PortalContainerContext';
import { isEventOutsideElement } from '../ui/outsideClick';
import {
    clampSectionLinkMenuPosition,
    SECTION_LINK_CONTEXT_MENU_HEIGHT,
    SECTION_LINK_CONTEXT_MENU_WIDTH,
} from './sectionLinkContextMenuPlacement';

export interface SectionLinkContextMenuAnchor {
    clientX: number;
    clientY: number;
    headingId: string;
}

interface SectionLinkContextMenuProps {
    anchor: SectionLinkContextMenuAnchor;
    shareUrl: string;
    onClose: () => void;
}

/**
 * Portal-rendered context menu for copying a share-link to a heading section.
 * Mounts only when the user right-clicks a heading; the parent controls
 * lifecycle by conditionally rendering this component.
 */
export const SectionLinkContextMenu: React.FC<SectionLinkContextMenuProps> = ({
    anchor,
    shareUrl,
    onClose,
}) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const portalContainer = usePortalContainer();

    useEffect(() => {
        const onKeyDown = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') {
                onClose();
            }
        };
        const onPointerDown = (ev: MouseEvent) => {
            if (isEventOutsideElement(ev, menuRef.current)) {
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [onClose]);

    const position = useMemo(
        () =>
            clampSectionLinkMenuPosition({
                clientX: anchor.clientX,
                clientY: anchor.clientY,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
                menuWidth: SECTION_LINK_CONTEXT_MENU_WIDTH,
                menuHeight: SECTION_LINK_CONTEXT_MENU_HEIGHT,
            }),
        [anchor.clientX, anchor.clientY],
    );

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            onClose();
        } catch (error) {
            console.error('Failed to copy section link:', error);
        }
    };

    if (!portalContainer) {
        return null;
    }

    return createPortal(
        <div
            ref={menuRef}
            className="skill-panel-context-menu"
            role="menu"
            style={{
                position: 'fixed',
                left: position.left,
                top: position.top,
            }}
        >
            <button
                type="button"
                className="skill-panel-menu-item"
                role="menuitem"
                onClick={handleCopy}
            >
                Copy link to this section
            </button>
        </div>,
        portalContainer,
    );
};
